import {
  Accordion,
  Badge,
  Col,
  FloatingLabel,
  Row,
  Table,
  Modal,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";

import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import forecasts from "../../../assets/data/forecasts.json";
import axios from "axios";
import moment from "moment/moment";

export default function AllFamilyFundForecast() {

  // =========================================================
  // STATES
  // =========================================================

  const [show, setShow] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  const [reqs, setReqs] = useState([]);

  const [data, setData] = useState({
    thead: [],
    tbody: [],
  });

  // Only this accordion body will render
  const [activeAccordion, setActiveAccordion] = useState("0");

  // Loading state
  const [loading, setLoading] = useState(false);

  // API error
  const [apiError, setApiError] = useState("");

  // Filter error
  const [error, setError] = useState("");

  const emptyFilters = {
    rfq_id: "",
    donor_id: "",
    status_id: "",
    from_date: "",
    to_date: "",
  };

  // By default only the current month's requests are loaded
  const currentMonthFilters = {
    ...emptyFilters,
    from_date: moment().startOf("month").format("YYYY-MM-DD"),
    to_date: moment().endOf("month").format("YYYY-MM-DD"),
  };

  const [filterValues, setFilterValues] = useState(currentMonthFilters);

  // Filters used for the current list, kept while moving between pages
  const [appliedFilters, setAppliedFilters] = useState({});

  // Filter options sent with the list
  const [donors, setDonors] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  // Families are loaded only when a request is opened, cached by request id
  const [familiesByRequest, setFamiliesByRequest] = useState({});
  const [loadingFamilies, setLoadingFamilies] = useState(false);

  const AuthToken = localStorage.getItem("AuthUser");


  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {

    setData(
      forecasts || {
        thead: [],
        tbody: [],
      }
    );

    fetchReqs({
      from_date: currentMonthFilters.from_date,
      to_date: currentMonthFilters.to_date,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // =========================================================
  // FETCH REQUESTS
  // =========================================================

  const fetchReqs = async (params = {}, page = 1) => {

    try {

      setLoading(true);
      setApiError("");

      const response = await axios.get(
        process.env.REACT_APP_API_URL + "/list-forecast",
        {
          params: {
            ...params,
            page,
          },
          headers: {
            Authorization: `Bearer ${AuthToken}`,
          },
        }
      );

      const result =
        response?.data?.family_requests || [];

      setReqs(result);
      setAppliedFilters(params);
      setFamiliesByRequest({});

      if (response?.data?.pagination) {
        setPagination(response.data.pagination);
      }

      setDonors(response?.data?.donors || []);
      setStatuses(response?.data?.statuses || []);

      // Open first accordion after load
      if (result.length > 0) {
        setActiveAccordion("0");
      } else {
        setActiveAccordion(null);
      }

    } catch (err) {

      console.error(
        "Error fetching forecasts list:",
        err
      );

      setReqs([]);

      setApiError(
        err?.response?.data?.message ||
        "Unable to load Family Fund Forecast."
      );

    } finally {

      setLoading(false);

    }
  };


  // =========================================================
  // FAMILIES OF THE OPEN REQUEST
  // =========================================================

  useEffect(() => {

    const openReq =
      activeAccordion !== null
        ? reqs[Number(activeAccordion)]
        : null;

    if (!openReq || familiesByRequest[openReq.id]) {
      return;
    }

    setLoadingFamilies(true);

    axios
      .get(
        process.env.REACT_APP_API_URL +
        `/list-forecast/${openReq.id}/families`,
        {
          headers: {
            Authorization: `Bearer ${AuthToken}`,
          },
        }
      )
      .then((response) => {

        setFamiliesByRequest((prev) => ({
          ...prev,
          [openReq.id]: response?.data?.families || [],
        }));

      })
      .catch((err) => {

        console.error(
          "Error fetching request families:",
          err
        );

      })
      .finally(() => setLoadingFamilies(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccordion, reqs]);


  // =========================================================
  // PAGE CHANGE
  // =========================================================

  const handlePageChange = (page) => {

    if (
      page < 1 ||
      page > pagination.last_page ||
      page === pagination.current_page
    ) {
      return;
    }

    fetchReqs(appliedFilters, page);
  };


  // =========================================================
  // FILTER CHANGE
  // =========================================================

  const handleChange = (e) => {

    const {
      name,
      value,
      type,
      files,
    } = e.target;

    const fieldValue =
      type === "file"
        ? files?.[0]
        : value;

    const updated = {
      ...filterValues,
      [name]: fieldValue,
    };

    setFilterValues(updated);

    const {
      from_date,
      to_date,
    } = updated;

    if (
      (
        from_date &&
        to_date &&
        new Date(to_date) >=
        new Date(from_date)
      ) ||
      (!from_date && !to_date)
    ) {
      setError("");
    }
  };


  // =========================================================
  // APPLY FILTER
  // =========================================================

  const handleApplyFilter = async (e) => {

    if (e?.preventDefault) {
      e.preventDefault();
    }

    const {
      from_date,
      to_date,
    } = filterValues;


    if (from_date && !to_date) {

      setError(
        "Please select a 'To Date' when 'From Date' is chosen."
      );

      return;
    }


    if (!from_date && to_date) {

      setError(
        "Please select a 'From Date' when 'To Date' is chosen."
      );

      return;
    }


    if (
      from_date &&
      to_date &&
      new Date(to_date) <
      new Date(from_date)
    ) {

      setError(
        "'To Date' cannot be earlier than 'From Date'."
      );

      return;
    }


    setError("");

    // Send only the filters that have a value
    const params = Object.fromEntries(
      Object.entries(filterValues).filter(
        ([, value]) => value !== ""
      )
    );

    await fetchReqs(params);
  };


  // =========================================================
  // CLEAR FILTER
  // =========================================================

  const handleClearFilter = async () => {

    setFilterValues(emptyFilters);

    setError("");
    setApiError("");

    await fetchReqs();
  };


  // =========================================================
  // IMAGE MODAL
  // =========================================================

  const handleShow = (imageSrc) => {

    if (!imageSrc) {
      return;
    }

    setCurrentImage(imageSrc);
    setShow(true);
  };


  const handleClose = () => {

    setShow(false);
    setCurrentImage(null);
  };


  // =========================================================
  // UI
  // =========================================================

  return (

    <PageLayout>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <Row>

        <Col xl={12}>

          <div className="mc-card">

            <div className="mc-breadcrumb">

              <h3 className="mc-breadcrumb-title">
                All Family Fund Forecast
              </h3>

              <ul className="mc-breadcrumb-list">

                <li className="mc-breadcrumb-item">

                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                    style={{
                      color: "#077834",
                    }}
                  >
                    home
                  </Link>

                </li>

                <li className="mc-breadcrumb-item">

                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                    style={{
                      color: "#077834",
                    }}
                  >
                    Finances
                  </Link>

                </li>

                <li className="mc-breadcrumb-item">
                  All Family Fund Forecast
                </li>

              </ul>

            </div>

          </div>

        </Col>

      </Row>


      {/* =====================================================
          FILTER
      ===================================================== */}

      <Row>

        <Col xl={12}>

          <div className="mc-card">

            <div
              className="mc-breadcrumb"
              style={{
                marginBottom: "10px",
              }}
            >

              <h3 className="mc-breadcrumb-title">
                Filter Requests
              </h3>

            </div>


            <Form onSubmit={handleApplyFilter}>

              <Row className="align-items-end mb-3">


                {/* REQUEST ID */}

                <Col md={4}>

                  <Form.Group>

                    <FloatingLabel label="Request ID">

                      <Form.Control
                        type="text"
                        name="rfq_id"
                        placeholder="PPI-1"
                        value={
                          filterValues.rfq_id
                        }
                        onChange={handleChange}
                        disabled={loading}
                      />

                    </FloatingLabel>

                  </Form.Group>

                </Col>


                {/* DONOR */}

                <Col md={4}>

                  <Form.Group>

                    <FloatingLabel label="Donor">

                      <Form.Select
                        name="donor_id"
                        value={
                          filterValues.donor_id
                        }
                        onChange={handleChange}
                        disabled={loading}
                      >

                        <option value="">
                          All Donors
                        </option>

                        {donors.map((donor) => (

                          <option
                            key={donor.id}
                            value={donor.id}
                          >
                            {donor.name}
                          </option>

                        ))}

                      </Form.Select>

                    </FloatingLabel>

                  </Form.Group>

                </Col>


                {/* STATUS */}

                <Col md={4}>

                  <Form.Group>

                    <FloatingLabel label="Status">

                      <Form.Select
                        name="status_id"
                        value={
                          filterValues.status_id
                        }
                        onChange={handleChange}
                        disabled={loading}
                      >

                        <option value="">
                          All Statuses
                        </option>

                        {statuses.map((status) => (

                          <option
                            key={status.id}
                            value={status.id}
                          >
                            {status.name}
                          </option>

                        ))}

                      </Form.Select>

                    </FloatingLabel>

                  </Form.Group>

                </Col>

              </Row>


              <Row className="align-items-end">


                {/* FROM DATE */}

                <Col md={4}>

                  <Form.Group>

                    <FloatingLabel label="From">

                      <Form.Control
                        type="date"
                        name="from_date"
                        value={
                          filterValues.from_date
                        }
                        onChange={handleChange}
                        disabled={loading}
                      />

                    </FloatingLabel>

                  </Form.Group>

                </Col>


                {/* TO DATE */}

                <Col md={4}>

                  <Form.Group>

                    <FloatingLabel label="To">

                      <Form.Control
                        type="date"
                        name="to_date"
                        value={
                          filterValues.to_date
                        }
                        onChange={handleChange}
                        disabled={loading}
                      />

                    </FloatingLabel>

                  </Form.Group>

                </Col>


                {/* SEARCH */}

                <Col md={2}>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    style={{
                      paddingTop: "10px",
                      fontWeight: "bold",
                      fontSize: "14px",
                      backgroundColor: "#077834",
                      borderColor: "#077834",
                    }}
                    className="w-100"
                  >

                    {loading ? (

                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading...
                      </>

                    ) : (

                      "Search"

                    )}

                  </Button>

                </Col>


                {/* CLEAR */}

                <Col md={2}>

                  <Button
                    variant="secondary"
                    onClick={handleClearFilter}
                    type="button"
                    disabled={loading}
                    style={{
                      paddingTop: "10px",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}
                    className="w-100"
                  >
                    Clear Filter
                  </Button>

                </Col>

              </Row>


              {/* FILTER ERROR */}

              {error && (

                <div
                  className="text-danger mt-2"
                  style={{
                    fontSize: "13px",
                  }}
                >
                  {error}
                </div>

              )}

            </Form>

          </div>

        </Col>

      </Row>


      {/* =====================================================
          API ERROR
      ===================================================== */}

      {apiError && (

        <Row>

          <Col xl={12}>

            <div className="alert alert-danger">

              {apiError}

            </div>

          </Col>

        </Row>

      )}


      {/* =====================================================
          MAIN LOADING
      ===================================================== */}

      {loading ? (

        <Row>

          <Col xl={12}>

            <div
              className="mc-card"
              style={{
                minHeight: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <div className="text-center">

                <Spinner
                  animation="border"
                  role="status"
                  style={{
                    width: "3rem",
                    height: "3rem",
                  }}
                />

                <div
                  style={{
                    marginTop: "15px",
                    fontWeight: "600",
                  }}
                >
                  Loading Family Fund Forecast...
                </div>

              </div>

            </div>

          </Col>

        </Row>

      ) : (

        <>

          {/* =================================================
              NO RECORDS
          ================================================= */}

          {reqs.length === 0 && !apiError ? (

            <Row>

              <Col xl={12}>

                <div className="mc-card text-center">

                  <div
                    style={{
                      padding: "40px",
                    }}
                  >
                    No records found.
                  </div>

                </div>

              </Col>

            </Row>

          ) : (

            /* ===============================================
               ACCORDION
            =============================================== */

            <Row>

              <Col xl={12}>

                <Accordion
                  activeKey={activeAccordion}
                  onSelect={(eventKey) => {

                    setActiveAccordion(
                      eventKey
                    );

                  }}
                >

                  {reqs.map(
                    (req, index) => {

                      /*
                       * Summary total is calculated on the server.
                       * Families are loaded only for the open request.
                       */

                      const totalFamilyAmount =
                        Number(req.total_amount || 0);

                      const families =
                        familiesByRequest[req.id];


                      return (

                        <Accordion.Item
                          key={req.id}
                          eventKey={index.toString()}
                          className="custom-accordion"
                        >

                          {/* ===============================
                              ACCORDION HEADER
                          =============================== */}

                          <Accordion.Header>

                            <Table
                              bordered
                              hover
                              size="sm"
                              className="
                                text-center
                                mb-0
                                head-table
                                table-responsive
                              "
                            >

                              <thead className="table-light">

                                <tr>

                                  <th>
                                    REQ-ID
                                  </th>

                                  <th>
                                    DONOR
                                  </th>

                                  <th>
                                    TOTAL FAMILIES
                                  </th>

                                  <th>
                                    TOTAL AMOUNT
                                  </th>

                                  <th>
                                    STATUS
                                  </th>

                                  <th>
                                    DATE REQUESTED
                                  </th>

                                  <th>
                                    ACTION
                                  </th>

                                </tr>

                              </thead>


                              <tbody>

                                <tr className="border rounded">

                                  <td>
                                    {req.rfq_id}
                                  </td>

                                  <td>
                                    {
                                      req.loan_donor
                                        ?.name || "-"
                                    }
                                  </td>

                                  <td>
                                    {
                                      req.families_count || 0
                                    }
                                    {req.requested_families
                                      ? ` / ${req.requested_families}`
                                      : ""}
                                  </td>

                                  <td>
                                    {
                                      totalFamilyAmount
                                    }
                                  </td>

                                  <td>

                                    <Badge
                                      bg={
                                        req.status_id === 4
                                          ? "success"
                                          : req.status_id === 5
                                            ? "danger"
                                            : "warning"
                                      }
                                    >
                                      {
                                        req.status
                                          ?.name
                                      }
                                    </Badge>

                                  </td>

                                  <td>

                                    {moment(
                                      req.created_at
                                    ).format(
                                      "DD-MM-YYYY"
                                    )}

                                  </td>

                                  <td>

                                    <Link
                                      to={`/edit-family-fund-forecast/${req.id}`}
                                    >

                                      <i
                                        className="material-icons text-center"
                                        title="Edit"
                                      >
                                        edit_note
                                      </i>

                                    </Link>

                                  </td>

                                </tr>

                              </tbody>

                            </Table>

                          </Accordion.Header>


                          {/* ===============================
                              ACCORDION BODY

                              IMPORTANT:
                              ONLY ACTIVE BODY IS RENDERED
                          =============================== */}

                          <Accordion.Body>

                            {activeAccordion ===
                              index.toString() && !families && (

                              <div className="text-center p-3">

                                {loadingFamilies ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                  />
                                ) : (
                                  "No families found."
                                )}

                              </div>

                            )}

                            {activeAccordion ===
                              index.toString() && families && (

                              <div className="mc-table-responsive">

                                <table className="mc-table product">

                                  <thead className="mc-table-head primary">

                                    <tr>

                                      {data?.thead?.map(
                                        (
                                          item,
                                          idx
                                        ) => (

                                          <th
                                            key={
                                              idx
                                            }
                                            style={{
                                              padding:
                                                "0.5rem 1rem",
                                            }}
                                          >
                                            {
                                              item
                                            }
                                          </th>

                                        )
                                      )}

                                    </tr>

                                  </thead>


                                  <tbody className="mc-table-body even">

                                    {families.map(
                                      (
                                        item,
                                        idx
                                      ) => {

                                        const loanAmount =
                                          Number(
                                            item.person
                                              ?.loan
                                              ?.loan_amount ||
                                            0
                                          );

                                        const resettlementAmount =
                                          Number(
                                            item.person
                                              ?.resettlement_amount ||
                                            0
                                          );

                                        const estimatedCost =
                                          Number(
                                            item.person
                                              ?.loan
                                              ?.estimated_business_cost ||
                                            0
                                          );

                                        const scholarshipAmount =
                                          Number(
                                            item.person
                                              ?.loan
                                              ?.scholarship_amount ||
                                            0
                                          );

                                        const totalAmount =
                                          loanAmount +
                                          resettlementAmount +
                                          estimatedCost +
                                          scholarshipAmount;


                                        return (

                                          <tr
                                            key={
                                              item.id ||
                                              idx
                                            }
                                          >

                                            {/* REQUEST */}

                                            <td>
                                              {
                                                req.rfq_id
                                              }
                                            </td>


                                            {/* FAMILY NUMBER */}

                                            <td>
                                              {
                                                item.person
                                                  ?.f_number
                                              }
                                            </td>


                                            {/* BRICK KILN */}

                                            <td>
                                              {
                                                item.person
                                                  ?.brick_kiln
                                                  ?.brickkiln_name
                                              }
                                            </td>


                                            {/* OWNER */}

                                            <td>
                                              {
                                                item.person
                                                  ?.brick_kiln
                                                  ?.brickkiln_owner
                                              }
                                            </td>


                                            {/* OWNER CELL */}

                                            <td>
                                              {
                                                item.person
                                                  ?.brick_kiln
                                                  ?.brickkiln_owner_cell
                                              }
                                            </td>


                                            {/* OWNER CNIC */}

                                            <td>
                                              {
                                                item.person
                                                  ?.brick_kiln
                                                  ?.owner_cnic
                                              }
                                            </td>


                                            {/* IDENTIFIER */}

                                            <td>
                                              {
                                                item.person
                                                  ?.identifier
                                                  ?.name
                                              }
                                            </td>


                                            {/* BENEFICIARY */}

                                            <td>
                                              {
                                                item.person
                                                  ?.beneficiary_name
                                              }
                                            </td>


                                            {/* CNIC */}

                                            <td>
                                              {
                                                item.person
                                                  ?.cnic
                                              }
                                            </td>


                                            {/* INCENTIVE */}

                                            <td>
                                              {
                                                item.person
                                                  ?.loan
                                                  ?.incentive
                                              }
                                            </td>


                                            {/* LOAN */}

                                            <td>

                                              {
                                                loanAmount
                                              }


                                              {item.person
                                                ?.loan
                                                ?.unpaid_loan_invoice && (

                                                <>

                                                  <hr />

                                                  <i
                                                    className="material-icons text-center"
                                                    title="View Invoice"
                                                    style={{
                                                      cursor:
                                                        "pointer",
                                                    }}
                                                    onClick={() =>
                                                      handleShow(
                                                        process.env
                                                          .REACT_APP_IMG_URL +
                                                        item
                                                          .person
                                                          .loan
                                                          .unpaid_loan_invoice
                                                      )
                                                    }
                                                  >
                                                    receipt_long
                                                  </i>

                                                </>

                                              )}

                                            </td>


                                            {/* RESETTLEMENT */}

                                            <td>
                                              {
                                                resettlementAmount
                                              }
                                            </td>


                                            {/* BUSINESS COST */}

                                            <td>
                                              {
                                                estimatedCost
                                              }
                                            </td>


                                            {/* SCHOLARSHIP */}

                                            <td>
                                              {
                                                scholarshipAmount
                                              }
                                            </td>


                                            {/* TOTAL */}

                                            <td>
                                              {
                                                totalAmount
                                              }
                                            </td>

                                          </tr>

                                        );

                                      }
                                    )}

                                  </tbody>

                                </table>

                              </div>

                            )}

                          </Accordion.Body>

                        </Accordion.Item>

                      );

                    }
                  )}

                </Accordion>


                {/* =========================================
                    PAGINATION
                ========================================= */}

                <div className="mc-paginate mt-3">

                  <p className="mc-paginate-title">
                    Page <b>{pagination.current_page}</b> of{" "}
                    <b>{pagination.last_page}</b>
                    {" "}({pagination.total} requests)
                  </p>

                  <ul className="mc-paginate-list">

                    <li
                      className="mc-paginate-item"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        handlePageChange(
                          pagination.current_page - 1
                        )
                      }
                    >
                      <i className="material-icons">
                        chevron_left
                      </i>
                    </li>

                    {[...Array(pagination.last_page)]
                      .map((_, idx) => idx + 1)
                      .filter(
                        (page) =>
                          page === 1 ||
                          page === pagination.last_page ||
                          Math.abs(page - pagination.current_page) <= 2
                      )
                      .map((page) => (

                        <li
                          key={page}
                          className="mc-paginate-item"
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              page === pagination.current_page
                                ? "#077834"
                                : "#e8f5e9",
                            color:
                              page === pagination.current_page
                                ? "#fff"
                                : "#077834",
                          }}
                          onClick={() =>
                            handlePageChange(page)
                          }
                        >
                          {page}
                        </li>

                      ))}

                    <li
                      className="mc-paginate-item"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        handlePageChange(
                          pagination.current_page + 1
                        )
                      }
                    >
                      <i className="material-icons">
                        chevron_right
                      </i>
                    </li>

                  </ul>

                </div>

              </Col>

            </Row>

          )}

        </>

      )}


      {/* =====================================================
          INVOICE MODAL
      ===================================================== */}

      <Modal
        show={show}
        size="lg"
        onHide={handleClose}
        centered
      >

        <Modal.Header closeButton>

          <Modal.Title>
            Invoice Preview
          </Modal.Title>

        </Modal.Header>


        <Modal.Body>

          {currentImage ? (

            <img
              src={currentImage}
              alt="Invoice"
              className="img-fluid"
            />

          ) : (

            <p>
              No Image Available
            </p>

          )}

        </Modal.Body>


        <Modal.Footer>

          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Close
          </Button>

        </Modal.Footer>

      </Modal>

    </PageLayout>

  );
}